import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap, Tag, Repeat, BookOpen, Search, DollarSign, CreditCard, Info, CheckCircle, Calendar, Folder } from 'react-feather';

const RulesInfoPage: React.FC = () => {
  const navigate = useNavigate();

  const examples = [
    {
      title: 'Netflix prenumeration',
      description: 'Matchar: "netflix", "NETFLIX.COM"',
      category: 'Underhållning',
      recurring: true,
      interval: 'Månatlig',
    },
    {
      title: 'ICA butik',
      description: 'Matchar: "ica", "ICA SUPERMARKET"',
      category: 'Mat & Dryck',
      recurring: false,
    },
    {
      title: 'SL månadsbiljett',
      description: 'Matchar: "sl kort", "SL TRAFIK"',
      category: 'Transport',
      recurring: true,
      interval: 'Månatlig',
    },
    {
      title: 'Lön',
      description: 'Matchar: belopp över 20000 kr',
      category: 'Inkomst',
      recurring: true,
      interval: 'Månatlig',
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="page-header bg-white shadow-sm border-b border-gray-200">
        <div className="page-header-content px-4 py-4">
          <div className="flex items-center">
            <button onClick={() => navigate('/settings/rules')} className="mr-4">
              <span className="text-xl">‹</span>
            </button>
            <h1 className="text-xl font-bold text-gray-900">Hur regler fungerar</h1>
          </div>
        </div>
      </div>

      <div className="page-content px-4 py-6">
        {/* Vad är regler */}
        <div className="bg-white rounded-xl p-5 shadow-sm mb-4">
          <div className="flex items-center mb-3">
            <Zap size={24} className="text-blue-500 mr-3" />
            <h2 className="text-lg font-semibold text-gray-900">Vad är regler?</h2>
          </div>
          <p className="text-gray-700 leading-relaxed">
            Regler hjälper dig att automatiskt kategorisera dina transaktioner. 
            Istället för att manuellt välja kategori för varje transaktion, 
            kan du skapa regler som gör det automatiskt.
          </p>
        </div>

        {/* Vanliga regler */}
        <div className="bg-white rounded-xl p-5 shadow-sm mb-4">
          <div className="flex items-center mb-3">
            <Tag size={24} className="text-green-500 mr-3" />
            <h2 className="text-lg font-semibold text-gray-900">Vanliga regler</h2>
          </div>
          <p className="text-gray-700 leading-relaxed mb-4">
            Dessa regler analyserar dina transaktioner och kategoriserar dem automatiskt. 
            De matchar baserat på:
          </p>
          <div className="space-y-2 mb-4">
            <div className="flex items-center">
              <Search size={16} className="text-gray-500 mr-3" />
              <span className="text-gray-700">Text i beskrivningen</span>
            </div>
            <div className="flex items-center">
              <DollarSign size={16} className="text-gray-500 mr-3" />
              <span className="text-gray-700">Belopp-intervall</span>
            </div>
            <div className="flex items-center">
              <CreditCard size={16} className="text-gray-500 mr-3" />
              <span className="text-gray-700">Specifikt konto</span>
            </div>
          </div>
          <p className="text-gray-700 leading-relaxed">
            <span className="font-semibold">Exempel:</span> Skapa en regel som matchar alla transaktioner 
            med "netflix" i beskrivningen och kategoriserar dem som "Underhållning".
          </p>
        </div>

        {/* Återkommande regler */}
        <div className="bg-white rounded-xl p-5 shadow-sm mb-4">
          <div className="flex items-center mb-3">
            <Repeat size={24} className="text-yellow-500 mr-3" />
            <h2 className="text-lg font-semibold text-gray-900">Återkommande regler</h2>
          </div>
          <p className="text-gray-700 leading-relaxed mb-4">
            Dessa regler gör allt som vanliga regler gör, PLUS de skapar prognoser 
            för när nästa transaktion väntas komma.
          </p>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <Info size={20} className="text-blue-500 mr-3 mt-0.5" />
              <p className="text-blue-800 text-sm leading-relaxed">
                <span className="font-semibold">Viktigt:</span> Återkommande regler skapar INTE nya transaktioner. 
                De analyserar bara dina befintliga transaktioner för att förutsäga framtida betalningar.
              </p>
            </div>
          </div>
        </div>

        {/* Exempel */}
        <div className="bg-white rounded-xl p-5 shadow-sm mb-4">
          <div className="flex items-center mb-4">
            <BookOpen size={24} className="text-purple-500 mr-3" />
            <h2 className="text-lg font-semibold text-gray-900">Exempel på regler</h2>
          </div>
          <div className="space-y-3">
            {examples.map((example, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-gray-900">{example.title}</h3>
                  {example.recurring && (
                    <div className="flex items-center bg-blue-50 text-blue-600 px-2 py-1 rounded-full">
                      <Repeat size={12} className="mr-1" />
                      <span className="text-xs font-medium">Återkommande</span>
                    </div>
                  )}
                </div>
                <p className="text-gray-600 text-sm mb-2">{example.description}</p>
                <div className="flex items-center gap-4">
                  <div className="flex items-center">
                    <Folder size={14} className="text-gray-500 mr-1" />
                    <span className="text-sm text-gray-700">{example.category}</span>
                  </div>
                  {example.recurring && (
                    <div className="flex items-center">
                      <Calendar size={14} className="text-gray-500 mr-1" />
                      <span className="text-sm text-gray-700">{example.interval}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Tips */}
        <div className="bg-white rounded-xl p-5 shadow-sm">
          <div className="flex items-center mb-4">
            <Info size={24} className="text-yellow-500 mr-3" />
            <h2 className="text-lg font-semibold text-gray-900">Tips för bästa resultat</h2>
          </div>
          <div className="space-y-3">
            <div className="flex items-start">
              <CheckCircle size={16} className="text-green-500 mr-3 mt-0.5" />
              <p className="text-gray-700 leading-relaxed">
                Använd specifika text-matchningar istället för generella ord
              </p>
            </div>
            <div className="flex items-start">
              <CheckCircle size={16} className="text-green-500 mr-3 mt-0.5" />
              <p className="text-gray-700 leading-relaxed">
                Skapa återkommande regler för prenumerationer och fasta utgifter
              </p>
            </div>
            <div className="flex items-start">
              <CheckCircle size={16} className="text-green-500 mr-3 mt-0.5" />
              <p className="text-gray-700 leading-relaxed">
                Använd belopp-intervall för att undvika felaktiga matchningar
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RulesInfoPage;
